/**
 * SectionDropSlotContext
 *
 * Provides a render-function for DropSlot to Section Block renderers.
 * This avoids the circular-dependency chain:
 *   ContentBlocks -> SectionBlocks -> ContentBlocks
 *
 * ContentBlocks.tsx provides the function via <SectionDropSlotProvider>,
 * and SectionBlocks / StoreSectionBlocks consume it with useSectionDropSlot().
 */
import { createContext, useContext } from "react";
import type { ReactNode } from "react";

type DropSlotRenderer = (id: string) => ReactNode;

const SectionDropSlotCtx = createContext<DropSlotRenderer | null>(null);

export const SectionDropSlotProvider = SectionDropSlotCtx.Provider;

/**
 * Hook consumed inside section-block renderers.
 * Returns a function `(id: string) => ReactNode` that renders a DropSlot,
 * or a no-op that returns null when the provider is absent.
 */
export function useSectionDropSlot(): DropSlotRenderer {
  const fn = useContext(SectionDropSlotCtx);
  return fn ?? (() => null);
}
